import { create } from 'zustand';
import type {
  EventProject, EventType, ChatMessage, OnboardingStep, ChatGuideStep,
  Milestone, ShoppingItem, Tip, LedgerEntry, Invitation, Todo, God, GodName, ActionStatus,
} from '../types';
import { GODS, getEventTitle } from '../data/mockData';
import { api, setToken as setApiToken } from '../lib/api';

interface User {
  id: number;
  email: string;
  nickname: string;
  avatar: string;
  createdAt: string;
}

// === 后端返回类型（仅用于映射，不导出）===
interface ApiEvent {
  id: number;
  eventType: string;
  title: string;
  description?: string;
  status: string;
  budget: string | number;
  eventDate: string | null;
  createdAt: string;
}
interface ApiChatMessage {
  id: number;
  eventId: number;
  sender: string;
  kind: string;
  text: string;
  actionTool?: string;
  actionPayload?: any;
  actionStatus?: string;
  createdAt: string;
}
interface ApiMilestone {
  id: number;
  eventId: number;
  title: string;
  date: string;
  daysBefore: number;
  done: boolean;
  godId: string;
  sortOrder: number;
}
interface ApiShoppingItem {
  id: number;
  eventId: number;
  name: string;
  category: string;
  price: string | number;
  quantity: number;
  cpsUrl: string;
  purchased: boolean;
  emoji: string;
  sortOrder: number;
}
interface ApiSuggestion {
  id: number;
  eventId: number;
  phase: string;
  title: string;
  content: string;
  sortOrder: number;
}
interface ApiLedgerEntry {
  id: number;
  eventId: number;
  type: string;
  category: string;
  amount: string | number;
  note: string;
  createdAt: string;
}
interface ApiInvitation {
  id: number;
  eventId: number;
  template: string;
  title: string;
  content: string;
  location: string;
  date: string;
  shareUrl: string;
  createdAt: string;
}
interface ApiTodo {
  id: number;
  userId: number;
  eventId: number | null;
  title: string;
  description: string;
  completed: boolean;
  priority: number;
  dueDate: string | null;
  createdAt: string;
}
interface ApiGod {
  id: string;
  name: string;
  avatar: string;
  image: string;
  domains: string[];
  domainDesc: string;
  intro: string;
  sortOrder: number;
}

// === 映射工具 ===
function mapStatus(s: string): EventProject['status'] {
  if (s === 'ongoing') return 'active';
  if (s === 'completed') return 'done';
  return 'planning';
}

function toEventProject(e: ApiEvent): EventProject {
  return {
    id: e.id,
    type: e.eventType as EventType,
    title: e.title,
    status: mapStatus(e.status),
    createdAt: e.createdAt ? e.createdAt.slice(0, 10) : '',
    targetDate: e.eventDate ? e.eventDate.slice(0, 10) : '',
    budget: Number(e.budget) || 0,
  };
}

function toChatMessage(m: ApiChatMessage): ChatMessage {
  return {
    id: m.id,
    god: m.sender as GodName | 'user' | 'system',
    kind: (m.kind === 'action' ? 'action' : 'text') as 'text' | 'action',
    text: m.text,
    timestamp: m.createdAt ? new Date(m.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
    actionTool: m.actionTool,
    actionPayload: m.actionPayload,
    actionStatus: m.actionStatus as ActionStatus | undefined,
  };
}

function toMilestone(m: ApiMilestone): Milestone {
  return {
    id: m.id,
    eventId: m.eventId,
    title: m.title,
    date: m.date ? m.date.slice(0, 10) : '',
    daysBefore: m.daysBefore,
    done: m.done,
    god: m.godId as GodName,
    sortOrder: m.sortOrder,
  };
}

function toShoppingItem(i: ApiShoppingItem): ShoppingItem {
  return {
    id: i.id,
    eventId: i.eventId,
    name: i.name,
    category: i.category,
    price: Number(i.price) || 0,
    quantity: i.quantity,
    cpsUrl: i.cpsUrl,
    purchased: i.purchased,
    emoji: i.emoji,
    sortOrder: i.sortOrder,
  };
}

function toTip(s: ApiSuggestion): Tip {
  return {
    id: s.id,
    eventId: s.eventId,
    phase: s.phase,
    title: s.title,
    content: s.content,
    sortOrder: s.sortOrder,
  };
}

function toLedgerEntry(l: ApiLedgerEntry): LedgerEntry {
  return {
    id: l.id,
    eventId: l.eventId,
    type: l.type as LedgerEntry['type'],
    category: l.category,
    amount: Number(l.amount) || 0,
    note: l.note || '',
    date: l.createdAt ? l.createdAt.slice(0, 10) : '',
  };
}

function toInvitation(v: ApiInvitation): Invitation {
  return {
    id: v.id,
    eventId: v.eventId,
    template: v.template,
    title: v.title,
    content: v.content,
    location: v.location,
    date: v.date,
    shareUrl: v.shareUrl,
  };
}

function toTodo(t: ApiTodo): Todo {
  return {
    id: t.id,
    eventId: t.eventId,
    title: t.title,
    description: t.description || '',
    completed: t.completed,
    priority: t.priority,
    dueDate: t.dueDate ? t.dueDate.slice(0, 10) : null,
    createdAt: t.createdAt ? t.createdAt.slice(0, 10) : '',
  };
}

function toGod(g: ApiGod): God {
  const fallback = (GODS as Record<string, God>)[g.id];
  return {
    id: g.id,
    name: g.name || fallback?.name || g.id,
    emoji: fallback?.emoji || '✨',
    color: fallback?.color || '#e63946',
    bgColor: fallback?.bgColor || '#fff3cd',
    title: fallback?.title || '',
    desc: g.domainDesc || fallback?.desc || '',
    avatar: g.avatar,
    image: g.image,
    domains: g.domains,
    domainDesc: g.domainDesc,
    intro: g.intro,
    sortOrder: g.sortOrder,
  };
}

interface AppState {
  // 用户
  username: string;
  setUsername: (name: string) => void;

  // 认证
  authToken: string | null;
  setAuthToken: (token: string | null) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;

  // 页面阶段
  stage: 'onboarding' | 'main';
  setStage: (s: 'onboarding' | 'main') => void;

  // 首次访问流程
  onboardingStep: OnboardingStep;
  setOnboardingStep: (s: OnboardingStep) => void;

  // 群聊引导流程
  chatGuideStep: ChatGuideStep;
  setChatGuideStep: (s: ChatGuideStep) => void;

  // 倒计时/预算更新标记
  countdownUpdated: boolean;
  budgetUpdated: boolean;
  setCountdownUpdated: (v: boolean) => void;
  setBudgetUpdated: (v: boolean) => void;

  // 神仙信息（运行时从 API 拉，GODS 作为 fallback）
  gods: Record<string, God>;
  loadGods: () => Promise<void>;

  // 项目（内存，从 API 拉）
  projects: EventProject[];
  currentProjectId: number | null;
  setCurrentProject: (id: number) => void;
  createProject: (type: EventType) => Promise<EventProject>;
  loadProjects: () => Promise<void>;

  // 项目数据更新
  updateProjectDate: (projectId: number, date: string) => Promise<void>;
  updateProjectBudget: (projectId: number, budget: number) => Promise<void>;
  updateProjectTitle: (projectId: number, title: string) => Promise<void>;

  // 聊天消息（按 eventId 存内存）
  messages: Record<number, ChatMessage[]>;
  addMessage: (eventId: number, msg: ChatMessage) => void;
  loadMessages: (eventId: number) => Promise<void>;
  updateMessageStatus: (eventId: number, messageId: number, status: ActionStatus) => void;
  resolveAction: (messageId: number, status: ActionStatus) => Promise<void>;

  // 各类数据（按 eventId 存内存，服务端是唯一事实源）
  milestones: Record<number, Milestone[]>;
  shoppingItems: Record<number, ShoppingItem[]>;
  tips: Record<number, Tip[]>;
  ledger: Record<number, LedgerEntry[]>;
  invitations: Record<number, Invitation[]>;
  todos: Record<number, Todo[]>;

  loadMilestones: (eventId: number) => Promise<void>;
  loadShoppingItems: (eventId: number) => Promise<void>;
  loadTips: (eventId: number) => Promise<void>;
  loadLedger: (eventId: number) => Promise<void>;
  loadInvitations: (eventId: number) => Promise<void>;
  loadTodos: (eventId: number) => Promise<void>;

  toggleMilestone: (eventId: number, milestoneId: number) => Promise<void>;
  toggleShoppingItem: (eventId: number, itemId: number) => Promise<void>;
  addLedgerEntry: (eventId: number, entry: { type: string; category: string; amount: number; note?: string }) => Promise<void>;

  // 待办 CRUD
  addTodo: (eventId: number, title: string, priority?: number, dueDate?: string) => Promise<void>;
  toggleTodo: (todoId: number, eventId: number) => Promise<void>;
  deleteTodo: (todoId: number, eventId: number) => Promise<void>;
}

// 从 localStorage 读取缓存状态
const cachedUsername = localStorage.getItem('xishia_username');
const cachedStage = localStorage.getItem('xishia_stage') as 'onboarding' | 'main' | null;
const cachedAuthToken = localStorage.getItem('xishia_authToken');
const cachedChatGuideStep = localStorage.getItem('xishia_chatGuideStep') as ChatGuideStep | null;
const cachedCurrentProjectId = localStorage.getItem('xishia_currentProjectId');
const cachedUser = localStorage.getItem('xishia_user');

export const useAppStore = create<AppState>((set, get) => ({
  username: cachedUsername || '',
  setUsername: (name) => {
    localStorage.setItem('xishia_username', name);
    set({ username: name });
  },

  authToken: cachedAuthToken || null,
  setAuthToken: (token) => {
    setApiToken(token);
    if (token) localStorage.setItem('xishia_authToken', token);
    else localStorage.removeItem('xishia_authToken');
    set({ authToken: token });
  },
  user: cachedUser ? JSON.parse(cachedUser) : null,
  setUser: (user) => {
    if (user) localStorage.setItem('xishia_user', JSON.stringify(user));
    else localStorage.removeItem('xishia_user');
    set({ user });
  },
  logout: () => {
    setApiToken(null);
    localStorage.removeItem('xishia_authToken');
    localStorage.removeItem('xishia_user');
    localStorage.removeItem('xishia_currentProjectId');
    set({ authToken: null, user: null, currentProjectId: null, projects: [], messages: {} });
  },

  stage: cachedStage || 'onboarding',
  setStage: (s) => {
    localStorage.setItem('xishia_stage', s);
    set({ stage: s });
  },

  onboardingStep: 'god-question',
  setOnboardingStep: (s) => set({ onboardingStep: s }),

  chatGuideStep: cachedChatGuideStep || 'xishen-announce',
  setChatGuideStep: (s) => {
    localStorage.setItem('xishia_chatGuideStep', s);
    set({ chatGuideStep: s });
  },

  countdownUpdated: false,
  budgetUpdated: false,
  setCountdownUpdated: (v) => set({ countdownUpdated: v }),
  setBudgetUpdated: (v) => set({ budgetUpdated: v }),

  gods: { ...GODS },
  loadGods: async () => {
    try {
      const res = await api.get<{ list: ApiGod[] }>('/god-info');
      const map: Record<string, God> = { ...GODS };
      for (const g of res.list) map[g.id] = toGod(g);
      set({ gods: map });
    } catch {
      /* fallback 已在内存 */
    }
  },

  projects: [],
  currentProjectId: cachedCurrentProjectId ? Number(cachedCurrentProjectId) : null,
  setCurrentProject: (id) => {
    localStorage.setItem('xishia_currentProjectId', String(id));
    set({ currentProjectId: id });
  },

  createProject: async (type) => {
    const title = getEventTitle(type);
    const event = await api.post<ApiEvent>('/events', { eventType: type, title });
    const project = toEventProject(event);
    set((state) => ({
      projects: [...state.projects, project],
      currentProjectId: project.id,
      chatGuideStep: 'xishen-announce',
    }));
    localStorage.setItem('xishia_currentProjectId', String(project.id));
    return project;
  },

  loadProjects: async () => {
    try {
      const res = await api.get<{ list: ApiEvent[] }>('/events');
      set({ projects: res.list.map(toEventProject) });
    } catch {
      /* 未登录或网络错误，保持空 */
    }
  },

  updateProjectDate: async (projectId, date) => {
    await api.put(`/events/${projectId}`, { eventDate: new Date(date).toISOString() });
    set((state) => ({
      projects: state.projects.map((p) => (p.id === projectId ? { ...p, targetDate: date } : p)),
      countdownUpdated: true,
    }));
  },

  updateProjectBudget: async (projectId, budget) => {
    await api.put(`/events/${projectId}`, { budget });
    set((state) => ({
      projects: state.projects.map((p) => (p.id === projectId ? { ...p, budget } : p)),
      budgetUpdated: true,
    }));
  },

  updateProjectTitle: async (projectId, title) => {
    await api.put(`/events/${projectId}`, { title });
    set((state) => ({
      projects: state.projects.map((p) => (p.id === projectId ? { ...p, title } : p)),
    }));
  },

  messages: {},
  addMessage: (eventId, msg) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [eventId]: [...(state.messages[eventId] || []), msg],
      },
    })),

  loadMessages: async (eventId) => {
    try {
      const res = await api.get<{ list: ApiChatMessage[] }>(`/chat/${eventId}/messages`);
      set((state) => ({
        messages: { ...state.messages, [eventId]: res.list.map(toChatMessage) },
      }));
    } catch {
      /* ignore */
    }
  },

  updateMessageStatus: (eventId, messageId, status) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [eventId]: (state.messages[eventId] || []).map((m) =>
          m.id === messageId ? { ...m, actionStatus: status } : m,
        ),
      },
    })),

  resolveAction: async (messageId, status) => {
    await api.post(`/chat/messages/${messageId}/resolve`, { status });
    // 找到该消息所属 eventId 并更新本地状态
    const msgs = get().messages;
    for (const eventId of Object.keys(msgs)) {
      const list = msgs[Number(eventId)];
      if (list.some((m) => m.id === messageId)) {
        get().updateMessageStatus(Number(eventId), messageId, status);
        break;
      }
    }
  },

  milestones: {},
  shoppingItems: {},
  tips: {},
  ledger: {},
  invitations: {},
  todos: {},

  loadMilestones: async (eventId) => {
    try {
      const res = await api.get<{ list: ApiMilestone[] }>(`/events/${eventId}/milestones`);
      set((state) => ({ milestones: { ...state.milestones, [eventId]: res.list.map(toMilestone) } }));
    } catch {
      /* ignore */
    }
  },

  loadShoppingItems: async (eventId) => {
    try {
      const res = await api.get<{ list: ApiShoppingItem[] }>(`/events/${eventId}/shopping`);
      set((state) => ({ shoppingItems: { ...state.shoppingItems, [eventId]: res.list.map(toShoppingItem) } }));
    } catch {
      /* ignore */
    }
  },

  loadTips: async (eventId) => {
    try {
      const res = await api.get<{ suggestions: ApiSuggestion[] }>(`/events/${eventId}/dashboard`);
      set((state) => ({ tips: { ...state.tips, [eventId]: res.suggestions.map(toTip) } }));
    } catch {
      /* ignore */
    }
  },

  loadLedger: async (eventId) => {
    try {
      const res = await api.get<{ ledger: { entries: ApiLedgerEntry[] } }>(`/events/${eventId}/dashboard`);
      set((state) => ({ ledger: { ...state.ledger, [eventId]: res.ledger.entries.map(toLedgerEntry) } }));
    } catch {
      /* ignore */
    }
  },

  loadInvitations: async (eventId) => {
    try {
      const res = await api.get<{ list: ApiInvitation[] }>(`/events/${eventId}/invitations`);
      set((state) => ({ invitations: { ...state.invitations, [eventId]: res.list.map(toInvitation) } }));
    } catch {
      /* ignore */
    }
  },

  loadTodos: async (eventId) => {
    try {
      const res = await api.get<{ list: ApiTodo[] }>('/todos?pageSize=100');
      const list = res.list.filter((t) => t.eventId === eventId).map(toTodo);
      set((state) => ({ todos: { ...state.todos, [eventId]: list } }));
    } catch {
      /* ignore */
    }
  },

  toggleMilestone: async (eventId, milestoneId) => {
    const current = (get().milestones[eventId] || []).find((m) => m.id === milestoneId);
    if (!current) return;
    const next = !current.done;
    // 乐观更新
    set((state) => ({
      milestones: {
        ...state.milestones,
        [eventId]: (state.milestones[eventId] || []).map((m) =>
          m.id === milestoneId ? { ...m, done: next } : m,
        ),
      },
    }));
    try {
      await api.put(`/events/${eventId}/milestones/${milestoneId}`, { done: next });
    } catch {
      // 回滚
      set((state) => ({
        milestones: {
          ...state.milestones,
          [eventId]: (state.milestones[eventId] || []).map((m) =>
            m.id === milestoneId ? { ...m, done: current.done } : m,
          ),
        },
      }));
    }
  },

  toggleShoppingItem: async (eventId, itemId) => {
    const current = (get().shoppingItems[eventId] || []).find((i) => i.id === itemId);
    if (!current) return;
    const next = !current.purchased;
    set((state) => ({
      shoppingItems: {
        ...state.shoppingItems,
        [eventId]: (state.shoppingItems[eventId] || []).map((i) =>
          i.id === itemId ? { ...i, purchased: next } : i,
        ),
      },
    }));
    try {
      await api.put(`/events/${eventId}/shopping/${itemId}`, { purchased: next });
    } catch {
      set((state) => ({
        shoppingItems: {
          ...state.shoppingItems,
          [eventId]: (state.shoppingItems[eventId] || []).map((i) =>
            i.id === itemId ? { ...i, purchased: current.purchased } : i,
          ),
        },
      }));
    }
  },

  addLedgerEntry: async (eventId, entry) => {
    try {
      const created = await api.post<ApiLedgerEntry>(`/events/${eventId}/ledger`, {
        type: entry.type,
        category: entry.category,
        amount: entry.amount,
        note: entry.note || '',
      });
      set((state) => ({
        ledger: { ...state.ledger, [eventId]: [...(state.ledger[eventId] || []), toLedgerEntry(created)] },
      }));
    } catch {
      /* ignore */
    }
  },

  addTodo: async (eventId, title, priority = 0, dueDate) => {
    try {
      const created = await api.post<ApiTodo>('/todos', {
        eventId,
        title,
        priority,
        dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
      });
      set((state) => ({
        todos: { ...state.todos, [eventId]: [...(state.todos[eventId] || []), toTodo(created)] },
      }));
    } catch {
      /* ignore */
    }
  },

  toggleTodo: async (todoId, eventId) => {
    const current = (get().todos[eventId] || []).find((t) => t.id === todoId);
    if (!current) return;
    const next = !current.completed;
    set((state) => ({
      todos: {
        ...state.todos,
        [eventId]: (state.todos[eventId] || []).map((t) =>
          t.id === todoId ? { ...t, completed: next } : t,
        ),
      },
    }));
    try {
      await api.put(`/todos/${todoId}`, { completed: next });
    } catch {
      set((state) => ({
        todos: {
          ...state.todos,
          [eventId]: (state.todos[eventId] || []).map((t) =>
            t.id === todoId ? { ...t, completed: current.completed } : t,
          ),
        },
      }));
    }
  },

  deleteTodo: async (todoId, eventId) => {
    set((state) => ({
      todos: {
        ...state.todos,
        [eventId]: (state.todos[eventId] || []).filter((t) => t.id !== todoId),
      },
    }));
    try {
      await api.delete(`/todos/${todoId}`);
    } catch {
      /* ignore */
    }
  },
}));
